import { useCallback, useEffect, useMemo, useState } from 'react';
import { Col, Row } from 'react-bootstrap';

import { CIAppliedFilter, CIAppliedListFilter, SearchInput } from 'ccs-ui-components';
import { debounce } from 'lodash';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import JourneyAssociationList from './journey-list/JourneyAssociationList';
import ServiceAssociationList from './service-list/ServiceAssociationList';

import { AssociationListFilter } from '../../../../_components/association-filter/AssociationListFilter';
import { FILTER_TYPE, SORT_STRINGS, allStatusList, associationEventType } from '../../../../constants';
import useServiceEventAssociation from '../../../../hooks/events/association-list/useServiceEventAssociation';
import { IService, IServiceResponseModel } from '../../../../models';
import { initialState } from '../../../../store/reducer/services/ServiceListReducer';
import classes from './AssociationServicesWrapper.module.scss';

interface IAssociationSourcesWrapper {
  eventName: string,
  eventVersion: string
}
const AssociationServicesWrapper = ({ eventName, eventVersion }: IAssociationSourcesWrapper) => {
  const { t: translate } = useTranslation();
  const { pathname } = useLocation();
  const [searchTemp, setSearchTemp] = useState('');
  const [queryParams, setQueryParams] = useState<{ [key: string]: string }>({
    page: String(0),
    item: String(10),
    serviceName: '',
    status: '',
    sort: 'serviceName',
    type: SORT_STRINGS.ASC,
    eventType: ''
  });

  const [data, setData] = useState<IServiceResponseModel>(initialState?.serviceList);
  const debounceDelay = 300;
  const [selectedService, setSelectedService] = useState<IService>({ serviceName: '', endPoint: '', serviceType: '' });

  const serviceType = useMemo(() => (pathname.includes(associationEventType.INGRESS) ? associationEventType.INGRESS : associationEventType.EGRESS), [pathname]);

  const { serviceEventAssociation, isLoading, errorMessage } = useServiceEventAssociation();

  /**
   * Debounces the handler function and updates the query parameters.
   * @param value - The value to be passed to the handler function.
   */
  const debounceHandler = useCallback(debounce((value: string) => {
    setQueryParams((pre) => ({
      ...pre,
      status: queryParams.status,
      page: String(0),
      item: queryParams.item,
      serviceName: value
    }));
  }, debounceDelay), [searchTemp]);

  /**
 * @method searchHandler
 * @description Handles the search event and updates the search term state.
 * @param event - The change event triggered by the input element.
 */
  const searchHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTemp((event.target.value).trim());
    debounceHandler((event.target.value).trim());
  };

  const allStatusMap: { [key: string]: string } = {};
  allStatusList.forEach((s) => { allStatusMap[s.value] = s.label; });

  const appliedFilterData: CIAppliedFilter[] = useMemo(() => {
    const data: CIAppliedFilter[] = [];
    const filterStatusList: string[] = queryParams?.status?.split(',');
    if (!queryParams?.status) {
      return [];
    }
    if (filterStatusList?.length > 0 && filterStatusList?.length < allStatusList.length) {
      filterStatusList?.forEach((f) => data.push({ text: allStatusMap[f], type: FILTER_TYPE.STATUS, value: f }));
    }
    return data;
  }, [queryParams]);

  /**
   * Handles the change event of the applied filters.
   * @param filters - The array of CIAppliedFilter objects representing the applied filters.
   */
  const appliedFilterChangeHandler = (filters: CIAppliedFilter[]) => {
    const statusList: string[] = [];
    filters.forEach((f) => {
      if (f.type === FILTER_TYPE.STATUS) {
        statusList?.push(f.value);
      }
    });

    setQueryParams((pre) => ({
      ...pre,
      status: statusList?.join(',') ?? '',
    }));
  };

  useEffect(() => {
    setQueryParams((pre) => ({
      ...pre,
      page: String(0),
      item: String(10),
      eventType: serviceType ?? associationEventType.INGRESS,
    }));
  }, [serviceType]);

  useEffect(() => {
    if (queryParams.eventType) {
      serviceEventAssociation(queryParams, eventName, eventVersion)?.then(async (serviceAssociationRecords) => {
        if (serviceAssociationRecords.serviceAssociationRecords) {
          setData(serviceAssociationRecords.serviceAssociationRecords);
          if (serviceAssociationRecords.serviceAssociationRecords.services.length > 0) {
            setSelectedService(serviceAssociationRecords.serviceAssociationRecords.services[0]);
          }
        }
      });
    }
  }, [queryParams]);

  return (
    <>
      <div className={classes.filters}>
        <div className={classes['search-filter-div']}>
          <AssociationListFilter
            queryParams={queryParams}
            setQueryParams={setQueryParams}
          />
          <div className={classes.search}>
            <SearchInput
              label="search"
              type="search"
              placeholder={String(translate('service_list_wrapper_component.txt_search_service'))}
              onChange={searchHandler}
              data-testid="filterSearchInput"
              value={searchTemp}
            />
          </div>
        </div>
        <CIAppliedListFilter className={classes['applied-filters']} totalRecords={data?.totalRecords} appliedFilters={appliedFilterData} onFilterChange={appliedFilterChangeHandler} showResetFiltersButton={appliedFilterData.length > 0} />
      </div>
      <Row className={`${classes['association-service-wrapper']}`}>
        <Col md={6} id="service-association-list-wrapper">
          <ServiceAssociationList serviceData={data} setSelectedService={setSelectedService} selectedService={selectedService} setQueryParams={setQueryParams} queryParams={queryParams} errorMessage={errorMessage} isLoading={isLoading} />
        </Col>
        <Col md={6} className={`${classes['association-journey-list-wrapper']}`}>
          <JourneyAssociationList selectedService={selectedService} queryParams={queryParams} />
        </Col>
      </Row>
    </>
  );
};

export default AssociationServicesWrapper;
